import {useInfiniteQuery} from "@tanstack/react-query";
import {Button} from "@aiw/ui";
import {authHeaders,fetchImpl} from "../lib/api";
import {env} from "../lib/env";
import {useT} from "../lib/i18n";

/**
 * NEWEST N, THEN SHOW MORE — the CRM's one long-list pattern (13 Oct 2026).
 *
 * The API sorts newest first and pages with `limit` + an opaque `cursor` (crm-list-page.ts in
 * @artemis-group/aiw-contracts). Counts and totals come with each page and describe the WHOLE filtered
 * list, so a tab or a group header never counts only what happens to be loaded. Filters are request
 * parameters: a page held in the browser cannot be filtered into the truth.
 * Patterned on components/activity-history.tsx (newest first, then "Show more").
 */
export type ListPage<T,E={}> = {data:T[];next_cursor?:string|null;total?:number}&E;

export function useNewestList<T,E extends object={}>(path:string,params:Record<string,string|undefined>,opts:{pageSize?:number;enabled?:boolean;refetchInterval?:number}={}){
 const pageSize=opts.pageSize??50;
 const q=useInfiniteQuery({
  queryKey:["newest",path,params,pageSize],
  enabled:opts.enabled??true,
  refetchInterval:opts.refetchInterval,
  initialPageParam:null as string|null,
  queryFn:async({pageParam,signal})=>{
   const search=new URLSearchParams(Object.entries(params).filter((e):e is [string,string]=>!!e[1]));
   search.set("page_size",String(pageSize));if(pageParam)search.set("cursor",pageParam);
   const r=await fetchImpl(`${env.apiBase}${path}?${search}`,{headers:await authHeaders(),signal});
   const b=await r.json().catch(()=>null) as (ListPage<T,E>&{error?:string})|null;
   if(!r.ok||!b||!Array.isArray(b.data))throw new Error(b?.error??"This list could not be loaded");
   return b;
  },
  getNextPageParam:last=>last.next_cursor??undefined,
 });
 const pages=q.data?.pages??[];
 const rows=pages.flatMap(p=>p.data);
 const first=pages[0];
 return {query:q,rows,first,total:first?.total??rows.length,hasMore:!!q.hasNextPage,more:()=>void q.fetchNextPage()};
}

/** "Showing 50 of 312, newest first" and the button that fetches the next page. Says nothing while the list is whole. */
export function ShowMore({shown,total,hasMore,loading,failed,onMore,className}:{shown:number;total:number;hasMore:boolean;loading?:boolean;failed?:boolean;onMore:()=>void;className?:string}){
 const {t}=useT();
 if(!hasMore&&!failed)return null;
 return <div className={`flex flex-wrap items-center gap-3 py-3 text-sm ${className??""}`}>
  <span className="text-muted-foreground">{t('list.showing',{shown:String(shown),total:String(total)})}</span>
  {failed?<><span role="alert" className="text-warning">{t('list.moreFailed')}</span><Button size="sm" variant="outline" onClick={onMore}>{t('list.retry')}</Button></>
   :<Button size="sm" variant="outline" disabled={loading} onClick={onMore}>{loading?t('list.loadingMore'):t('list.showMore',{remaining:String(Math.max(0,total-shown))})}</Button>}
 </div>;
}

/** The footer for a `useNewestList` result. */
export function ListMore({list,className}:{list:ReturnType<typeof useNewestList<any,any>>;className?:string}){
 return <ShowMore className={className} shown={list.rows.length} total={list.total} hasMore={list.hasMore} loading={list.query.isFetchingNextPage} failed={list.query.isFetchNextPageError} onMore={list.more}/>;
}
